import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { Field } from "../../components/ui/Field";
import { ErrorText, Panel } from "../../components/ui/feedback";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { useI18n } from "../../i18n/I18nProvider";
import { apiRequest } from "../../lib/api";
import type { Invite, Role } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { inviteLinkFromApiUrl } from "./queries";

export interface CreatedInvite {
  invite: Invite;
  url: string;
}

export interface InviteFormProps {
  workspaceId: string;
  /** Receives the one-time invitation link. The parent shows it and refreshes the lists. */
  onCreated: (link: string) => void;
}

/** Board panel to invite a co-operator: email, role, and one primary action. */
export function InviteForm({ workspaceId, onCreated }: InviteFormProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");

  const create = useMutation({
    mutationFn: () =>
      apiRequest<CreatedInvite>(paths.invites(workspaceId), {
        method: "POST",
        body: { email: email.trim(), role },
      }),
    onSuccess: (result) => {
      setEmail("");
      onCreated(inviteLinkFromApiUrl(result.url, window.location.origin));
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (email.trim()) create.mutate();
  };

  return (
    <Panel className="space-y-4">
      <h2 className="font-display text-xl font-extrabold tracking-[0.01em] text-ink uppercase">
        {t("members.invite")}
      </h2>
      <form
        onSubmit={submit}
        className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto] sm:items-end"
      >
        <Field label={t("members.email")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label={t("members.role")}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={role}
              onChange={(event) => setRole(event.target.value === "owner" ? "owner" : "member")}
            >
              <option value="member">{t("common.role.member")}</option>
              <option value="owner">{t("common.role.owner")}</option>
            </Select>
          )}
        </Field>
        <Button type="submit" variant="primary" loading={create.isPending}>
          {t("members.inviteButton")}
        </Button>
      </form>
      <ErrorText error={create.error} />
    </Panel>
  );
}
